import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { CustomToast, ToastType } from './custom_toast';

interface QueuedToastInput {
  message: string;
  type?: ToastType;
  duration?: number;
}

interface QueuedToast extends QueuedToastInput {
  id: string;
}

interface ToastQueueContextValue {
  pushToast: (toast: QueuedToastInput) => void;
}

const ToastQueueContext = createContext<ToastQueueContextValue | null>(null);

/**
 * Opt-in shared toast queue: wrap a subtree (or the whole app) in this
 * provider to get stacked, auto-dismissing toasts via useToastQueue().
 * Existing call sites that render <CustomToast> directly with their own
 * local state are untouched and keep working exactly as before — this is
 * additive, not a replacement.
 */
export function ToastQueueProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<QueuedToast[]>([]);

  const pushToast = useCallback((toast: QueuedToastInput) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, ...toast }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastQueueContext.Provider value={{ pushToast }}>
      {children}
      <div className="fixed bottom-4 right-20 z-[1000] flex flex-col-reverse gap-3">
        {toasts.map((toast) => (
          <CustomToast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            positionClassName="relative"
            disablePortal
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastQueueContext.Provider>
  );
}

export function useToastQueue(): ToastQueueContextValue {
  const ctx = useContext(ToastQueueContext);
  if (!ctx) {
    throw new Error('useToastQueue must be used within a ToastQueueProvider');
  }
  return ctx;
}
